<template>
  <div
    ref="adminConsolePanel"
    class="panel-main"
  >
    <!-- Sections Panel -->
    <div class="panel-icons">
      <div
        v-for="option in adminOptions"
        :key="option.panelName"
      >
        <Tooltip
          v-if="option.isActive"
          placement="right"
          :content="option.title"
          :class="panelIsActive(option.panelName)"
          @click.native="showPanel(option.panelName)"
        >
          <i :class="panelIcon(option.icon)" />
        </Tooltip>
      </div>
    </div>

    <!-- Content Panel -->
    <div class="panel-content">
      <!-- Login Notices -->
      <LoginNotices
        v-if="currentPanel.panelName === loginNotice"
        :title="currentPanel.title"
      />
      <!-- Cache Manager -->
      <CacheManager
        v-else-if="currentPanel.panelName === cacheManager"
        :title="currentPanel.title"
      />
      <!-- Dashboard Manager -->
      <DashboardManager
        v-else-if="currentPanel.panelName === dashboardManager"
        :title="currentPanel.title"
      />
      <!-- Golden Query Manager -->
      <GoldenQueryManager
        v-else-if="currentPanel.panelName === goldenQueryManager"
        :title="currentPanel.title"
      />
      <!-- Restricted Modules Manager -->
      <RestrictedModules
        v-else-if="currentPanel.panelName === usersPrivileges"
        :title="currentPanel.title"
      />
      <template v-else>
        <div
          ref="content"
          class="content"
        >
          <iframe
            v-show="currentPanel.panelName === adminDashboard"
            src="https://tab.limeinternal.com/views/DataPortalReadership/UsageOverview?iframeSizedToWindow=true&:embed=yes&:toolbar=top"
            allowfullscreen
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import LoginNotices from './helper-components/admin-console/LoginNotices'
import CacheManager from './helper-components/admin-console/CacheManager'
import DashboardManager from './helper-components/admin-console/DashboardManager'
import GoldenQueryManager from './helper-components/admin-console/GoldenQueryManager'
import RestrictedModules from './helper-components/admin-console/RestrictedModules'
import UserMixin from '@/mixins/UserMixin'

export default {
  components: {
    LoginNotices,
    CacheManager,
    DashboardManager,
    GoldenQueryManager,
    RestrictedModules
  },

  mixins: [
    UserMixin
  ],

  data () {
    return {
      adminDashboard: ADMIN_DASHBOARD,
      loginNotice: LOGIN_NOTICE,
      preferencesUsers: PREFERENCES_USERS,
      preferencesRegions: PREFERENCES_REGIONS,
      cacheManager: CACHE_MANAGER,
      dashboardManager: DASHBOARD_MANAGER,
      goldenQueryManager: GOLDEN_QUERY_MANAGER,
      scheduledQuery: SCHEDULED_QUERY,
      bugsReport: BUGS_REPORT,
      usersRoles: USERS_ROLES,
      usersPrivileges: USERS_PRIVILEGES,
      serverSettings: SERVER_SETTINGS,
      adminOptions: [
        {
          title: 'Admin Dashboard',
          panelName: ADMIN_DASHBOARD,
          icon: 'fas fa-chart-pie',
          isActive: true
        },
        {
          title: 'Login Notices',
          panelName: LOGIN_NOTICE,
          icon: 'fas fa-comment-alt',
          isActive: true
        },
        {
          title: 'Cache Manager',
          panelName: CACHE_MANAGER,
          icon: 'fas fa-hdd',
          isActive: true
        },
        {
          title: 'Dashboard Manager',
          panelName: DASHBOARD_MANAGER,
          icon: 'fas fa-chart-line',
          isActive: true
        },
        {
          title: 'Golden Query Manager',
          panelName: GOLDEN_QUERY_MANAGER,
          icon: 'fas fa-book',
          isActive: true
        },
        {
          title: 'Users Privileges',
          panelName: USERS_PRIVILEGES,
          icon: 'fas fa-user-shield',
          isActive: true
        },
        {
          title: 'Preferences for Users',
          panelName: PREFERENCES_USERS,
          icon: 'fas fa-users-cog',
          isActive: false
        },
        {
          title: 'Preferences for Regions',
          panelName: PREFERENCES_REGIONS,
          icon: 'fas fa-globe-americas',
          isActive: false
        },
        {
          title: 'Scheduled Query Manager',
          panelName: SCHEDULED_QUERY,
          icon: 'fas fa-calendar-check',
          isActive: false
        },
        {
          title: 'Bugs Report (Sentry)',
          panelName: BUGS_REPORT,
          icon: 'fas fa-bug',
          isActive: false
        },
        {
          title: 'Users Roles',
          panelName: USERS_ROLES,
          icon: 'fas fa-user-tag',
          isActive: false,
          addLabel: 'Add New Module'
        },
        {
          title: 'Server Settings',
          panelName: SERVER_SETTINGS,
          icon: 'fas fa-server',
          isActive: false
        }
      ],
      activePanel: this.$root.preferences.adminConsole.activePanel || ADMIN_DASHBOARD
    }
  },

  computed: {
    currentPanel () {
      const selectedPanel = this.adminOptions.find(option => option.panelName === this.activePanel)
      return selectedPanel
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('adminConsole')) {
      this.$set(this.$root.preferences, 'adminConsole', {})
    }

    sessionStorage.setItem('context', 'adminConsole')
  },

  mounted () {
    const panelExists = this.adminOptions.find(option => option.panelName === this.activePanel)
    if (!panelExists) {
      this.activePanel = ADMIN_DASHBOARD
      this.$set(this.$root.preferences.adminConsole, 'activePanel', ADMIN_DASHBOARD)
    }
    this.validateUser()
  },

  methods: {
    /**
     * Displays the proper icon into the icons panel.
     * @param {String} icon
     * @return {String}
     */
    panelIcon (icon) {
      return `${icon} action-icon`
    },

    /**
     * Highlights an icon in icons panel if it is active.
     * @param {String} panelName
     * @return {String}
     */
    panelIsActive (panelName) {
      return `icon-block${this.currentPanel.panelName === panelName ? ' active' : ''}`
    },

    /**
     * Shows an specific content according with the selected panel option.
     * @param {String} panelName
     */
    showPanel (panelName) {
      this.activePanel = panelName
      this.$set(this.$root.preferences.adminConsole, 'activePanel', panelName)
    },

    /**
     * Verifies the current user is allowed to accesss the Admin Console.
     * If the user isn't allowed, it will be redirect to HOME
     */
    async validateUser () {
      const user = this.$auth.getUser()
      this.fetchAdminConsoleUsers()

      if (!this.adminConsoleUsers.includes(user.email)) {
        this.$router.push('/home')
        return
      }

      this.setPanelsContent()
    },

    /**
     * Sets the content of each panel.
     */
    setPanelsContent () {
      this.showPanel(this.activePanel)
    }
  }
}

const ADMIN_DASHBOARD = 'admin-dashboard'
const LOGIN_NOTICE = 'login-notice'
const PREFERENCES_USERS = 'preferences-users'
const PREFERENCES_REGIONS = 'preferences-regions'
const CACHE_MANAGER = 'cache-manager'
const DASHBOARD_MANAGER = 'dashboards-manager'
const GOLDEN_QUERY_MANAGER = 'golden-query-manager'
const SCHEDULED_QUERY = 'scheduled-queries-manager'
const BUGS_REPORT = 'bugs-report'
const USERS_ROLES = 'users-roles'
const USERS_PRIVILEGES = 'users-privileges'
const SERVER_SETTINGS = 'server-settings'
</script>

<style scoped>
  .action-icon {
    width: 24px;
    text-align: center;
    font-size: 16px;
    color: var(--color-black);
    cursor: pointer;
  }

  .action-icon.active {
    color: var(--color-success);
  }

  .action-icon.inactive {
    color: var(--color-red-border);
  }

  .capitalize {
    text-transform: capitalize;
  }

  .filter .dropdown .elements .row .input {
    width: 100%;
  }

  .panel-main {
    width: 100%;
    height: 100%;
    position: absolute;
    display: flex;
    flex-direction: row;
    border: 1px solid var(--color-gray-border);
  }

  .panel-main .panel-icons,
  .panel-main .panel-content {
    height: 100%;
  }

  .panel-main .panel-icons {
    padding-top: 2px;
    width: 32px;
    background-color: var(--color-light-gray);
    display: flex;
    flex-direction: column;
  }

  .panel-main .panel-icons .icon-block {
    margin: 0;
    display: flex;
    justify-content: center;
  }

  .panel-main .panel-icons .icon-block .action-icon {
    margin: 0 auto;
    padding: 8px 0;
    color: var(--color-gray-button);
  }

  .panel-main .panel-icons .icon-block.active .action-icon {
    color: var(--color-black);
  }

  .panel-main .panel-content {
    width: calc(100% - 32px);
    position: relative;
    padding: 8px;
    background-color: var(--color-white);
  }

  .panel-main .panel-content .title {
    margin-bottom: 8px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .panel-main .panel-content .title h2 {
    font-size: 16px;
    width: 100%;
  }

  .panel-main .panel-content .title .actions {
    width: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .panel-main .panel-content .content {
    width: 100%;
    height: calc(100% - 38px);
    overflow: auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .panel-main .panel-content iframe {
    width: 100%;
    height: 100%;
    position: initial;
    top: 6px;
    left: 0;
    border: 0;
    overflow: hidden;
  }

  .panel-main .panel-content .workspace {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .icon-tooltip {
    margin: 0 4px;
  }
</style>
